/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  Menu,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { CustomIconButton } from '@opengeoweb/shared';
import type { CoreAppStore } from '@opengeoweb/store';
import {
  mapSelectors,
  linkComponentTypesConstants,
  genericActions,
  genericSelectors,
  mapActions,
  constructSyncSourceDisplayName,
  getTakenWarningTimelineIds,
  isWarningsTimelineLinkSourceAllowed,
} from '@opengeoweb/store';
import { LinkOff, Link } from '@opengeoweb/theme';
import type { AppStore } from '../../../store/types';
import {
  getViewsById,
  getViewComponentType,
} from '../../../store/workspace/selectors';
import { useWorkspaceTranslation } from '../../../utils/i18n';

interface SelectMenuProps {
  panelId: string;
}

const LinkMenuConnect: React.FC<SelectMenuProps> = ({ panelId }) => {
  const dispatch = useDispatch();
  const { t } = useWorkspaceTranslation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const maps = useSelector((store: CoreAppStore) =>
    mapSelectors.getAllMapIds(store),
  );
  const viewsById = useSelector(getViewsById);

  const selectedTargetIds = useSelector((store: AppStore) =>
    genericSelectors.getLinkedTargetIds(store, panelId),
  );

  const linksByPanelId = useSelector((store: AppStore) =>
    genericSelectors.selectFeatureLinksForAllPanels(store),
  );

  const viewComponentType = useSelector((store: AppStore) =>
    getViewComponentType(store, panelId),
  );

  const sharedFeature = useSelector((store: CoreAppStore) =>
    genericSelectors.getSelectedFeature(store.syncGroups!, panelId),
  );

  const mapPinLocation = useSelector((store: CoreAppStore) =>
    mapSelectors.getPinLocation(store, selectedTargetIds[0] || ''),
  );

  const warningPanels = React.useMemo(() => {
    if (!isWarningsTimelineLinkSourceAllowed(viewComponentType)) {
      return [];
    }

    return Object.keys(viewsById || {}).filter((viewId) => {
      if (viewId === panelId) {
        return false;
      }

      return viewsById[viewId]?.componentType === 'WarningsTimeline';
    });
  }, [panelId, viewComponentType, viewsById]);

  const takenWarningTimelineIds = React.useMemo(
    () =>
      getTakenWarningTimelineIds(linksByPanelId, viewsById || {}, {
        excludedWarningListPanelId: panelId,
      }),
    [linksByPanelId, panelId, viewsById],
  );

  const shouldShowWarningPanels =
    viewComponentType === 'WarningList' && warningPanels.length > 0;

  const handleLinkViews = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (): void => {
    setAnchorEl(null);
  };

  const handleTargetSelect = (targetId: string): void => {
    const updatedTargetIds = selectedTargetIds.includes(targetId)
      ? selectedTargetIds.filter((id) => id !== targetId)
      : [...selectedTargetIds, targetId];

    dispatch(
      genericActions.updateLinkedMap({
        panelId,
        mapIds: updatedTargetIds,
      }),
    );

    // Sync pin to newly added map for TimeSeries or Soundings components
    if (
      maps.includes(targetId) &&
      !selectedTargetIds.includes(targetId) &&
      (viewComponentType === 'TimeSeries' || viewComponentType === 'Soundings')
    ) {
      // Observation station
      if (sharedFeature) {
        dispatch(
          mapActions.setMapPinLocation({
            mapId: targetId,
            mapPinLocation: {
              lon: sharedFeature.lon,
              lat: sharedFeature.lat,
            },
          }),
        );
        // Grid
      } else if (mapPinLocation) {
        dispatch(
          mapActions.setMapPinLocation({
            mapId: targetId,
            mapPinLocation,
          }),
        );
      }
    }
  };

  return (
    <>
      {linkComponentTypesConstants.supportedComponentTypesForLinking.includes(
        viewComponentType,
      ) && (
        <CustomIconButton
          data-testid="icon-button"
          onClick={handleLinkViews}
          sx={{ marginLeft: 0.5, marginRight: 0.5 }}
          tooltipTitle={`Link ${viewComponentType}`}
        >
          {selectedTargetIds.length > 0 ? (
            <Link
              data-testid="link-icon"
              style={{
                fill: anchorEl ? 'white' : undefined,
                backgroundColor: anchorEl ? '#186DFF' : undefined,
              }}
            />
          ) : (
            <LinkOff
              data-testid="link-off-icon"
              style={{
                fill: anchorEl ? 'white' : undefined,
                backgroundColor: anchorEl ? '#186DFF' : undefined,
              }}
            />
          )}
        </CustomIconButton>
      )}
      <Menu
        anchorEl={anchorEl}
        data-testid="map-select-menu"
        id="map-select-menu"
        keepMounted
        onClose={handleMenuClose}
        open={Boolean(anchorEl)}
      >
        <Typography
          style={{ padding: '10px 15px 0', fontWeight: 'bold' }}
          variant="h6"
        >
          {t('workspace-link')} - {t('workspace-link-location-header')}
        </Typography>
        <List dense>
          {maps.map((mapId) => {
            const labelId = `checkbox-link-label-${mapId}`;
            const mapTitle = constructSyncSourceDisplayName(
              mapId,
              viewsById[mapId],
              viewsById[mapId]?.title || mapId,
            );

            return (
              <ListItem
                dense
                disablePadding
                divider
                key={mapId}
                onClick={() => handleTargetSelect(mapId)}
              >
                <ListItemButton
                  dense
                  role="checkbox"
                  sx={{ margin: 0, padding: '0 16px' }}
                >
                  <ListItemIcon>
                    <Checkbox
                      checked={selectedTargetIds.includes(mapId)}
                      disableRipple
                      edge="start"
                      slotProps={{
                        input: { 'aria-labelledby': labelId },
                      }}
                      tabIndex={-1}
                    />
                  </ListItemIcon>
                  <ListItemText id={labelId} primary={mapTitle} />
                </ListItemButton>
              </ListItem>
            );
          })}
          {shouldShowWarningPanels ? (
            <>
              {warningPanels.map((targetPanelId) => {
                const labelId = `checkbox-link-label-${targetPanelId}`;
                const panelTitle = constructSyncSourceDisplayName(
                  targetPanelId,
                  viewsById[targetPanelId],
                  viewsById[targetPanelId]?.title || targetPanelId,
                );
                const isSelected = selectedTargetIds.includes(targetPanelId);
                const isTaken =
                  takenWarningTimelineIds.has(targetPanelId) && !isSelected;

                return (
                  <ListItem
                    dense
                    disablePadding
                    divider
                    key={targetPanelId}
                    onClick={() => {
                      if (!isTaken) {
                        handleTargetSelect(targetPanelId);
                      }
                    }}
                  >
                    <ListItemButton
                      dense
                      disabled={isTaken}
                      role="checkbox"
                      sx={{ margin: 0, padding: '0 16px' }}
                    >
                      <ListItemIcon>
                        <Checkbox
                          checked={isSelected}
                          disableRipple
                          disabled={isTaken}
                          edge="start"
                          slotProps={{
                            input: { 'aria-labelledby': labelId },
                          }}
                          tabIndex={-1}
                        />
                      </ListItemIcon>
                      <ListItemText id={labelId} primary={panelTitle} />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </>
          ) : null}
        </List>
      </Menu>
    </>
  );
};

export default LinkMenuConnect;
